import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { School as SchoolIcon, Plus, Users as UsersIcon, ClipboardCheck, BookOpen, Megaphone, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { useI18n } from "@/context/I18nContext";

const ROLES = [
  { key: "student", label: "Student" },
  { key: "parent",  label: "Parent" },
  { key: "teacher", label: "Teacher" },
  { key: "admin",   label: "Admin" },
];
const TYPES = ["government", "private", "aided", "gurukul", "international", "coaching", "online"];

export default function Schools() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [mine, setMine] = useState([]);
  const [active, setActive] = useState(null);      // school_id we're viewing
  const [detail, setDetail] = useState(null);
  const [ann, setAnn] = useState([]);
  const [hw, setHw]   = useState([]);
  const [creating, setCreating] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joinRole, setJoinRole] = useState("parent");
  const [joinChild, setJoinChild] = useState("");
  const [joinGrade, setJoinGrade] = useState("");
  const [newSchool, setNewSchool] = useState({ name: "", type: "private", board: "CBSE", city: "" });
  const [annForm, setAnnForm] = useState({ title: "", body: "", audience: "all" });
  const [hwForm, setHwForm] = useState({ subject: "", grade: "", title: "", body: "", due_on: "" });

  useSeo({
    title: "Schools · find2near",
    description: "Universal parent-teacher-student portal — attendance, homework and announcements for every school.",
    canonical: "/schools",
  });

  const refresh = () => api.get("/schools/mine").then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { if (user) refresh(); }, [user]);

  const openSchool = async (school_id) => {
    setActive(school_id);
    const [d, a, h] = await Promise.all([
      api.get(`/schools/${school_id}`),
      api.get(`/schools/${school_id}/announcements`),
      api.get(`/schools/${school_id}/homework`),
    ]);
    setDetail(d.data);
    setAnn(a.data);
    setHw(h.data);
  };

  const createSchool = async () => {
    if (!newSchool.name.trim()) { toast.error("Name required"); return; }
    setCreating(true);
    try {
      const r = await api.post("/schools", newSchool);
      toast.success(`School created — code ${r.data.code}`);
      setNewSchool({ name: "", type: "private", board: "CBSE", city: "" });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  const joinSchool = async () => {
    if (!joinCode.trim()) { toast.error("Enter the 8-char school code"); return; }
    try {
      const r = await api.post("/schools/join", {
        code: joinCode.trim().toUpperCase(),
        role: joinRole,
        child_name: joinChild || undefined,
        grade: joinGrade || undefined,
      });
      toast.success(r.data.already_member ? "You're already in this school" : `Joined as ${r.data.role}`);
      setJoinCode(""); setJoinChild(""); setJoinGrade("");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  const postAnnouncement = async () => {
    if (!annForm.title.trim() || !annForm.body.trim()) return;
    try {
      await api.post("/schools/announcements", { school_id: active, ...annForm });
      setAnnForm({ title: "", body: "", audience: "all" });
      openSchool(active);
      toast.success("Posted");
    } catch (e) { toast.error(e.response?.data?.detail || "Post failed"); }
  };

  const postHomework = async () => {
    if (!hwForm.title.trim() || !hwForm.subject.trim() || !hwForm.grade.trim()) return;
    try {
      await api.post("/schools/homework", { school_id: active, ...hwForm });
      setHwForm({ subject: "", grade: "", title: "", body: "", due_on: "" });
      openSchool(active);
      toast.success("Homework posted");
    } catch (e) { toast.error(e.response?.data?.detail || "Post failed"); }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-6">
        <SchoolIcon className="mx-auto text-[#2D5A27]" size={44} />
        <h2 className="heading text-2xl font-extrabold mt-4">Sign in to join a school</h2>
        <p className="text-stone-500 mt-2 text-sm">Works for every school — private, government, gurukul, coaching.</p>
        <Link to="/onboarding" className="inline-block mt-6 bg-[#2D5A27] text-white px-6 py-3 rounded-full font-bold" data-testid="schools-signin">Get started</Link>
      </div>
    );
  }

  const canPost = detail && ["teacher", "admin"].includes(detail.role);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="schools-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2">
          <SchoolIcon size={12} /> Schools
        </div>
        <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">
          Every school, <span className="text-[#2D5A27]">one portal.</span>
        </h1>
        <p className="text-stone-500 text-sm mt-1 max-w-xl">
          {t("school.parents", "Parents")}, {t("school.teachers", "Teachers")} and {t("school.students", "Students")} — track homework, attendance and announcements together.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Join */}
        <div className="rounded-3xl bg-white border border-stone-200 p-5" data-testid="schools-join-card">
          <h2 className="heading text-lg font-black text-stone-900 mb-3">Join a school</h2>
          <div className="grid gap-2">
            <input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="8-char code (e.g. A3F91BEE)" className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none font-mono" data-testid="school-join-code" />
            <div className="grid grid-cols-4 gap-1">
              {ROLES.map((r) => (
                <button key={r.key} onClick={() => setJoinRole(r.key)} className={`text-xs font-bold py-2 rounded-full ${joinRole === r.key ? "bg-[#2D5A27] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`} data-testid={`school-join-role-${r.key}`}>{r.label}</button>
              ))}
            </div>
            {joinRole === "parent" && (
              <div className="grid grid-cols-2 gap-2">
                <input value={joinChild} onChange={(e) => setJoinChild(e.target.value)} placeholder="Child's name" className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="school-join-child" />
                <input value={joinGrade} onChange={(e) => setJoinGrade(e.target.value)} placeholder="Grade (e.g. 5)" className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="school-join-grade" />
              </div>
            )}
            <button onClick={joinSchool} className="bg-[#2D5A27] hover:bg-emerald-800 text-white font-black py-2.5 rounded-full mt-1" data-testid="school-join-submit">Join</button>
          </div>
        </div>

        {/* Create */}
        <div className="rounded-3xl bg-white border border-stone-200 p-5" data-testid="schools-create-card">
          <h2 className="heading text-lg font-black text-stone-900 mb-3">Register your school</h2>
          <div className="grid gap-2">
            <input value={newSchool.name} onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })} placeholder="School name" className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="school-name" />
            <div className="grid grid-cols-3 gap-2">
              <select value={newSchool.type} onChange={(e) => setNewSchool({ ...newSchool, type: e.target.value })} className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="school-type">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input value={newSchool.board} onChange={(e) => setNewSchool({ ...newSchool, board: e.target.value })} placeholder="Board" className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="school-board" />
              <input value={newSchool.city} onChange={(e) => setNewSchool({ ...newSchool, city: e.target.value })} placeholder="City" className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="school-city" />
            </div>
            <button onClick={createSchool} disabled={creating} className="bg-[#E05E36] hover:bg-[#c94e2a] text-white font-black py-2.5 rounded-full inline-flex items-center justify-center gap-1 disabled:opacity-50" data-testid="school-create-submit">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create school
            </button>
          </div>
        </div>
      </div>

      {/* My schools */}
      <div className="mt-6">
        <h2 className="heading text-lg font-black text-stone-900 mb-3">My schools</h2>
        {mine.length === 0 ? (
        <div className="text-xs text-stone-400 py-4 text-center" data-testid="schools-empty">You haven't joined any schools yet.</div>
        ) : (
          <ul className="grid gap-2">
            {mine.map((s) => (
              <li key={s.school_id} className="rounded-2xl bg-white border border-stone-200 p-4 flex items-center gap-3" data-testid={`school-mine-${s.school_id}`}>
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0"><SchoolIcon size={18} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900 truncate">{s.name}</div>
                  <div className="text-xs text-stone-500 truncate">{s.type} · {s.board || "—"} · role: <b className="text-stone-800">{s.my_role}</b>{s.my_grade ? ` · grade ${s.my_grade}` : ""}</div>
                </div>
                <span className="hidden md:inline font-mono text-[11px] text-stone-500">{s.code}</span>
                <button onClick={() => openSchool(s.school_id)} className="text-[#2D5A27] font-bold text-sm inline-flex items-center gap-1" data-testid={`school-open-${s.school_id}`}>Open <ArrowRight size={13} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Active school view */}
      {detail && (
        <div className="mt-6 rounded-3xl bg-white border border-stone-200 p-5" data-testid={`school-detail-${detail.school_id}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="heading text-xl font-black text-stone-900">{detail.name}</h3>
              <div className="text-xs text-stone-500">{detail.type} · {detail.city || "—"} · code <span className="font-mono">{detail.code}</span> · {detail.member_count} members</div>
            </div>
            <button onClick={() => { setActive(null); setDetail(null); }} className="text-stone-400 hover:text-stone-800 text-sm">Close</button>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Announcements */}
            <div>
              <h4 className="heading font-black text-stone-900 mb-2 flex items-center gap-2"><Megaphone size={16} className="text-[#E05E36]" /> {t("school.announcements", "Announcements")}</h4>
              {canPost && (
                <div className="rounded-2xl bg-stone-50 p-3 mb-3">
                  <input value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} placeholder="Title" className="w-full px-3 py-2 rounded-full border border-stone-200 text-sm mb-2 outline-none" data-testid="ann-title" />
                  <textarea value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} placeholder="What do parents / students need to know?" rows={2} className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm outline-none mb-2" data-testid="ann-body" />
                  <button onClick={postAnnouncement} className="bg-[#E05E36] hover:bg-[#c94e2a] text-white font-black text-sm px-3 py-1.5 rounded-full" data-testid="ann-submit">Post</button>
                </div>
              )}
              {ann.length === 0 ? (
                <div className="text-xs text-stone-400 py-4 text-center">No announcements.</div>
              ) : (
                <ul className="space-y-2">
                  {ann.map((a) => (
                    <li key={a.announcement_id} className="rounded-xl border border-stone-200 p-3">
                      <div className="font-bold text-stone-900">{a.title}</div>
                      <div className="text-[11px] text-stone-500">{a.author_name} · {new Date(a.created_at).toLocaleString()}</div>
                      <div className="text-sm text-stone-700 mt-1 whitespace-pre-wrap">{a.body}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Homework */}
            <div>
              <h4 className="heading font-black text-stone-900 mb-2 flex items-center gap-2"><BookOpen size={16} className="text-sky-600" /> {t("school.homework", "Homework")}</h4>
              {canPost && (
                <div className="rounded-2xl bg-stone-50 p-3 mb-3">
                  <div className="grid grid-cols-3 gap-2">
                    <input value={hwForm.subject} onChange={(e) => setHwForm({ ...hwForm, subject: e.target.value })} placeholder="Subject" className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="hw-subject" />
                    <input value={hwForm.grade} onChange={(e) => setHwForm({ ...hwForm, grade: e.target.value })} placeholder="Grade" className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="hw-grade" />
                    <input type="date" value={hwForm.due_on} onChange={(e) => setHwForm({ ...hwForm, due_on: e.target.value })} className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="hw-due" />
                  </div>
                  <input value={hwForm.title} onChange={(e) => setHwForm({ ...hwForm, title: e.target.value })} placeholder="Title" className="w-full px-3 py-2 rounded-full border border-stone-200 text-sm mt-2 outline-none" data-testid="hw-title" />
                  <textarea value={hwForm.body} onChange={(e) => setHwForm({ ...hwForm, body: e.target.value })} placeholder="Details / links" rows={2} className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm outline-none mt-2 mb-2" data-testid="hw-body" />
                  <button onClick={postHomework} className="bg-sky-600 hover:bg-sky-700 text-white font-black text-sm px-3 py-1.5 rounded-full" data-testid="hw-submit">Assign</button>
                </div>
              )}
              {hw.length === 0 ? (
                <div className="text-xs text-stone-400 py-4 text-center">No homework.</div>
              ) : (
                <ul className="space-y-2">
                  {hw.map((h) => (
                    <li key={h.homework_id} className="rounded-xl border border-stone-200 p-3">
                      <div className="text-[10px] uppercase tracking-widest font-black text-sky-700">{h.subject} · Grade {h.grade}</div>
                      <div className="font-bold text-stone-900">{h.title}</div>
                      <div className="text-xs text-stone-500">{h.due_on ? `Due ${h.due_on}` : "No due date"}</div>
                      <div className="text-sm text-stone-700 mt-1 whitespace-pre-wrap">{h.body}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
